import React from 'react';
import { Quote } from '../types/quote';

interface QuoteSummaryProps {
  quote: Quote;
}

const statusStyles: Record<string, string> = {
  Draft: 'bg-surface-container-high text-on-surface-variant',
  Sent: 'bg-primary-container text-on-primary-container',
  Accepted: 'bg-secondary-container text-on-secondary-container',
  Declined: 'bg-error-container text-on-error-container',
  Withdrawn: 'bg-surface-container-high text-on-surface-variant',
  Superseded: 'bg-surface-container-high text-on-surface-variant',
};

const currency = (n: number) => `R${n.toFixed(2)}`;

const QuoteSummary: React.FC<QuoteSummaryProps> = ({ quote }) => {
  return (
    <div className="p-3 rounded-lg bg-surface border border-outline-variant space-y-3 text-sm">
      <div className="flex items-center justify-between">
        <span className={`text-[11px] px-2 py-0.5 rounded-full font-label-bold ${statusStyles[quote.status] ?? 'bg-surface-container-high text-on-surface-variant'}`}>
          {quote.status}
        </span>
        <span className="text-xs text-outline">
          {quote.sentAt ? `Sent ${new Date(quote.sentAt).toLocaleDateString()}` : `Created ${new Date(quote.createdAt).toLocaleDateString()}`}
        </span>
      </div>

      {quote.tasks.length > 0 && (
        <div className="space-y-2">
          <h5 className="text-label-md font-label-bold text-on-surface-variant">Labour</h5>
          {quote.tasks.map((task) => (
            <div key={task.id} className="pl-2 border-l-2 border-outline-variant space-y-1">
              <div className="flex justify-between text-on-surface"><span>{task.description}</span><span>{currency(task.taskTotal)}</span></div>
              {task.resources.map((resource) => (
                <div key={resource.id} className="flex justify-between text-xs text-outline pl-2">
                  <span>{resource.description} ({resource.hours}h @ {currency(resource.ratePerHour)}/hr)</span>
                  <span>{currency(resource.total)}</span>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}

      {quote.materials.length > 0 && (
        <div className="space-y-1">
          <h5 className="text-label-md font-label-bold text-on-surface-variant">Materials</h5>
          {quote.materials.map((material) => (
            <div key={material.id} className="flex justify-between text-xs text-on-surface pl-2">
              <span>{material.description} ({material.quantity} x {currency(material.pricePerUnit)})</span>
              <span>{currency(material.total)}</span>
            </div>
          ))}
        </div>
      )}

      <div className="pt-2 border-t border-outline-variant space-y-1">
        <div className="flex justify-between text-xs text-outline"><span>Labour subtotal</span><span>{currency(quote.labourSubtotal)}</span></div>
        <div className="flex justify-between text-xs text-outline"><span>Materials subtotal</span><span>{currency(quote.materialsSubtotal)}</span></div>
        {quote.isVatRegistered && (
          <div className="flex justify-between text-xs text-outline"><span>VAT ({quote.vatRate}%)</span><span>{currency(quote.vatAmount)}</span></div>
        )}
        <div className="flex justify-between font-label-bold text-on-surface text-base"><span>Total</span><span>{currency(quote.total)}</span></div>
      </div>

      {quote.status === 'Declined' && quote.declineReason && (
        <p className="text-xs text-error">Declined: {quote.declineReason}</p>
      )}
    </div>
  );
};

export default QuoteSummary;
